import { faDownload, faFile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useServiceClient } from 'bz-stores/hooks/service-client';
import React from 'react';
import {
  GetFormFilesRequest,
  OnlineGetFormFilesRequest,
  type ResponseBase
} from 'tm3-core/api.dtos';

export type FormFileChannel = 'tm3' | 'online';
export const FormFileChannelContext = React.createContext<FormFileChannel>('tm3');

interface FormFile {
  listingId: string;
  name: string;
  path: string;
  extension: string;
  fileSizeBytes: number;
  createdDate: string;
  description: string;
}

interface FormFilesResponse {
  files: FormFile[];
}

interface Props {
  formId: string | null;
  tempFolderId: string | null;
}

const buildDownloadUrl = (channel: FormFileChannel, formId: string, listingId: string): string => {
  const params = new URLSearchParams({ listingId });
  const prefix = channel === 'online' ? '/api/online/form' : '/api/form';
  return `${prefix}/${encodeURIComponent(formId)}/files/download?${params.toString()}`;
};

const SubmittedFileList: React.FC<Props> = ({ formId, tempFolderId }) => {
  const client = useServiceClient();
  const channel = React.useContext(FormFileChannelContext);
  const [files, setFiles] = React.useState<FormFile[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!formId || !tempFolderId) return;
    setLoading(true);

    const fetchFiles = async (): Promise<FormFile[]> => {
      if (channel === 'online') {
        const res = await client.get<ResponseBase<FormFilesResponse>>(
          new OnlineGetFormFilesRequest({ formId, tempFolderId }) as any
        );
        return res.result?.files ?? [];
      }
      const res = await client.get<ResponseBase<FormFilesResponse>>(
        new GetFormFilesRequest({ formId: formId.toString(), tempFolderId }) as any
      );
      return res.result?.files ?? [];
    };

    fetchFiles()
      .then(setFiles)
      .catch((e) => {
        console.error(e);
        setFiles([]);
      })
      .finally(() => setLoading(false));
  }, [formId, tempFolderId, client, channel]);

  if (!formId) return null;
  if (loading) return <span className="text-sm text-neutral-400">Loading files…</span>;
  if (files.length === 0)
    return <span className="text-sm text-neutral-400">No files uploaded</span>;

  return (
    <div className="mt-1 flex flex-col gap-1" data-testid="submitted-file-list">
      {files.map((f) => (
        <div
          key={f.listingId}
          className="flex items-center gap-2 px-2 py-1.5 rounded border border-neutral-200 text-sm"
        >
          <FontAwesomeIcon icon={faFile} fixedWidth />
          <span className="flex-1 truncate">{f.name}</span>
          <a
            href={buildDownloadUrl(channel, formId, f.listingId)}
            download={f.name}
            className="shrink-0 text-primary hover:text-primary/80"
            aria-label={`Download ${f.name}`}
          >
            <FontAwesomeIcon icon={faDownload} />
          </a>
        </div>
      ))}
    </div>
  );
};

export default SubmittedFileList;
